import React, { useEffect } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter as Router, Redirect, Route, Switch } from "react-router-dom";
import { setRTL, setLanguage, MessageBarType, MessageBar, format, ThemeProvider, IMessageBarProps } from "@fluentui/react";
import { connect, Provider } from "react-redux";
import { AnyAction, Store } from "@reduxjs/toolkit";

import RcaTestListPage from "./components/RcaTestListPage";
import RcaNav from "./components/RcaNav";
import RcaHeader from "./components/RcaHeader";
import { RcaFooter } from "./components/RcaFooter";
import { IRcaState } from "./store/IRcaState";
import SettingsDialog from "./features/settings/SettingsDialog";
import TestResultPage from "./components/TestResultPage";
import TestInputPage from "./components/testinput/TestInputPage";
import * as globalActions from "./actions/globalConfigActions";
import { IRcaBootConfig } from "./boot";
import { IServices } from "./services/IServices";
import { getStore } from "./store/getStore";
import { ServiceProvider } from "./services/ServiceProvider";
import { TrustedHtmlComponent } from "./components/TrustedHtmlComponent";
import { useStringsSanitizer, useMediaQuery } from "./hooks";
import { Page } from "./components/Page";
import { applyTheme, getThemeForName, ThemeName } from "./services/ThemeService";

/**
 * The app component props
 */
interface IAppProps extends IAppStateProps, IAppDispatchProps {}

/**
 * The app component state props
 */
interface IAppStateProps {
  /**
   * The language direction
   */
  langDir: string;

  /**
   * The html language  https://www.w3schools.com/tags/ref_language_codes.asp
   */
  lang: string;

  /**
   * A list of banner IDs that should be shown.
   */
  showBanners: { [key: string]: boolean };

  /**
   * The user's theme preference
   */
  themeName: ThemeName;
}

/**
 * The app component dispatch props
 */
interface IAppDispatchProps {
  /**
   *
   * @param language - the user's language
   * @param elementId - the element ID in which the cookie banner should be rendered
   */
  initializeCookieBanner(language: string, elementId: string): void;

  /**
   * Calls the service to remember the dismissal of the banner
   *
   * @param id - The banner ID.
   */
  dismissBanner(id: string): void;
}

/**
 * Renders a persistent banner without live-region announcements.
 *
 * @param props - The Fluent UI message bar props.
 * @returns The static message bar component.
 */
const StaticMessageBar: React.FC<IMessageBarProps> = (props) => {
  const messageBarRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    messageBarRef.current?.querySelectorAll("[aria-live]").forEach((liveRegion) => {
      liveRegion.setAttribute("aria-live", "off");
    });
  });

  return <MessageBar {...props} ref={messageBarRef} role="none" delayedRender={false} />;
};

/**
 * The main Application component
 *
 * @param props - the app component props
 * @returns The component
 */
const AppComponent: React.FC<IAppProps> = ({ langDir, lang, showBanners, themeName, initializeCookieBanner, dismissBanner }) => {
  useEffect(() => {
    setLanguage(lang);
    setRTL(langDir === "rtl");
  }, [langDir, lang]);

  // Detect system preference changes for "system" theme mode
  const systemPrefersDark = useMediaQuery("(prefers-color-scheme: dark)");

  // Resolve the theme for rendering and apply global side effects after commit.
  const currentTheme = React.useMemo(() => {
    return getThemeForName(themeName);
  }, [themeName, systemPrefersDark]);

  useEffect(() => {
    applyTheme(themeName);
  }, [themeName, systemPrefersDark]);

  const cookieBannerElementId = "cookie-banner";

  useEffect(() => {
    // Initialize the cookie consent banner if needed
    initializeCookieBanner(lang, cookieBannerElementId);
  }, [lang]);

  const policy = useStringsSanitizer();

  return (
    <ThemeProvider theme={currentTheme} style={{ height: "100%" }}>
      <div
        style={{
          height: "100%",
          backgroundColor: currentTheme.semanticColors.bodyBackground,
          overflow: "hidden",
          display: "grid",
          gap: 0,
          gridTemplateColumns: "min-content auto",
          gridTemplateRows: "min-content min-content minmax(0, 1fr) min-content",
        }}
      >
        <div style={{ gridColumnStart: 1, gridColumnEnd: 3, gridRow: 1 }} id={cookieBannerElementId}></div>
        <div style={{ gridColumnStart: 1, gridColumnEnd: 3, gridRow: 2 }}>
          <RcaHeader />
          {showBanners["rcaipchange"] && (
            <StaticMessageBar
              messageBarType={MessageBarType.warning}
              onDismiss={() => dismissBanner("rcaipchange")}
              dismissButtonAriaLabel={ClientStrings.BannerCloseAria}
            >
              <TrustedHtmlComponent trustedHtml={policy.createHTML(ClientStrings.RcaIpChangeBannerMessage)} />
            </StaticMessageBar>
          )}
          {showBanners["basicauth"] && (
            <StaticMessageBar
              messageBarType={MessageBarType.warning}
              onDismiss={() => dismissBanner("basicauth")}
              dismissButtonAriaLabel={ClientStrings.BannerCloseAria}
            >
              <TrustedHtmlComponent trustedHtml={policy.createHTML(ClientStrings.BasicAuthBannerMessage)} />
            </StaticMessageBar>
          )}
          {showBanners["fasttrack"] && (
            <StaticMessageBar
              messageBarType={MessageBarType.info}
              onDismiss={() => dismissBanner("fasttrack")}
              dismissButtonAriaLabel={ClientStrings.BannerCloseAria}
              styles={{ innerText: { "& span a:last-child": { paddingInlineStart: 0 } } }}
            >
              <TrustedHtmlComponent trustedHtml={policy.createHTML(ClientStrings.FastTrackBannerMessage)} />
            </StaticMessageBar>
          )}
        </div>
        <div style={{ gridColumn: 1, gridRow: 3, minHeight: 0, overflowY: "auto" }}>
          <Route path="/" component={RcaNav} />
        </div>
        <main
          style={{
            gridColumn: 2,
            gridRow: 3,
            minHeight: 0,
            overflowY: "auto",
            backgroundColor: currentTheme.semanticColors.bodyBackground,
          }}
        >
          <Switch>
            <Redirect exact from="/" to="/tests" />
            <Redirect exact from="/tests" to="/tests/exo" />
            <Redirect exact from="/tests/o365" to="/tests/exo" />
            <Route
              path="/tests/:testId/input"
              render={(props) => (
                <Page title={format(WebsiteStrings.TestInputPageTitle, WebsiteStrings.ProductTitle)}>
                  <TestInputPage {...props} />
                </Page>
              )}
            />
            <Route
              path="/tests/:category"
              render={(props) => (
                <Page title={WebsiteStrings.ProductTitle}>
                  <RcaTestListPage {...props} />
                </Page>
              )}
            />
            <Route
              path="/result/:resultId"
              render={(props) => (
                <Page title={format(WebsiteStrings.TestResultsPageTitle, WebsiteStrings.ProductTitle)}>
                  <TestResultPage {...props} />
                </Page>
              )}
            />
          </Switch>
        </main>
        <div style={{ gridColumnStart: 1, gridColumnEnd: 3, gridRow: 4 }}>
          <RcaFooter />
        </div>
        <SettingsDialog />
      </div>
    </ThemeProvider>
  );
};

/**
 * The interval at which the RCA IP change banner should re-appear
 * Right now, we've decided it should reappear every 7 days.
 */
const rcaIpChangeReminderIntervalInMs = 7 * 24 * 60 * 60 * 1000;

/**
 * The interval at which the basic authentication reminder should re-appear
 * Right now, we've decided it should reappear every 7 days.
 */
const basicAuthReminderIntervalInMs = 7 * 24 * 60 * 60 * 1000;

/**
 * The interval at which the fast track banner should re-appear
 * Right now, we've decided it should reappear every 30 days.
 */
const fastTrackRecurrenceIntervalInMs = 30 * 24 * 60 * 60 * 1000;

/**
 * Redux connected component MapStateToProps function
 *
 * @param state - The application state
 * @returns The state props that will be used by the component
 */
const mapStateToProps = (state: IRcaState): IAppStateProps => {
  const rcaipchange =
    !state.globalConfig.bannersDismissed["rcaipchange"] ||
    new Date().valueOf() - state.globalConfig.bannersDismissed["rcaipchange"].valueOf() > rcaIpChangeReminderIntervalInMs;

  const basicauth =
    !state.globalConfig.bannersDismissed["basicauth"] ||
    new Date().valueOf() - state.globalConfig.bannersDismissed["basicauth"].valueOf() > basicAuthReminderIntervalInMs;

  const fasttrack =
    !state.globalConfig.bannersDismissed["fasttrack"] ||
    new Date().valueOf() - state.globalConfig.bannersDismissed["fasttrack"].valueOf() > fastTrackRecurrenceIntervalInMs;

  return {
    langDir: state.globalConfig.langDir,
    lang: state.globalConfig.language,
    showBanners: { basicauth, fasttrack, rcaipchange },
    themeName: state.globalConfig.themeName,
  };
};

/**
 * Redux connected component MapDispatchToProps object
 */
const mapDispatchToProps: IAppDispatchProps = {
  initializeCookieBanner: globalActions.initCookieConsent,
  dismissBanner: globalActions.dismissBanner,
};

// The redux connected component
export const App = connect(mapStateToProps, mapDispatchToProps)(AppComponent);

/**
 * Entrypoint for rendering the React application. Renders into "root"
 *
 * @param config - The boot configuration
 * @param services - The services
 */
export const renderApp = (config: IRcaBootConfig, services: IServices): void => {
  const store: Store<IRcaState, AnyAction> = getStore(config, services);
  const container = document.getElementById("root");
  const root = createRoot(container);
  root.render(
    <Provider store={store}>
      <ServiceProvider services={services}>
        <Router>
          <App />
        </Router>
      </ServiceProvider>
    </Provider>
  );
};
